import type { ApiResponse } from '~/types'
import { parseContactLocations } from '~/utils/contactLocations'
import { applyThemeCss, generateThemeCss, parseFooterLinks, type FooterLink } from '~/utils/theme'
import { parseFooterBadges } from '~/utils/footerBadges'
import { buildFooterSocialItems, parseFooterSocialLinks } from '~/utils/footerSocial'
import {
  DEFAULT_HOMEPAGE_FEATURE_CARDS,
  parseHomepageFeatureCards,
} from '~/utils/homepageFeatureCards'
import {
  DEFAULT_HOMEPAGE_SECTIONS,
  parseHomepageSections,
  type HomepageSections,
} from '~/utils/homepageSections'
import {
  DEFAULT_HOMEPAGE_CATEGORIES_DISPLAY,
  parseHomepageCategoriesDisplay,
} from '~/utils/homepageCategoriesDisplay'
import {
  EMPTY_GOOGLE_REVIEWS_DATA,
  parseGoogleReviewsData,
} from '~/utils/googleReviews'
import {
  parseGoogleReviewsEmbedUrl,
  parseGoogleReviewsMode,
} from '~/utils/googleReviewsDisplay'
import { enabledCheckoutTasks, parseCheckoutTasks } from '~/utils/checkoutTasks'
import { guessIconMimeType, resolveAbsoluteMediaUrl, resolveMediaUrl } from '~/utils/mediaUrl'
import { parseMetaPixelConfig } from '~/utils/metaPixel'
import { parseGoogleAnalyticsConfig } from '~/utils/googleAnalytics'
import { parseGoogleTagManagerConfig } from '~/utils/googleTagManager'
import { parseGoogleAdsConfig } from '~/utils/googleAds'
import { parseTawkToConfig } from '~/utils/tawkTo'
import { parseOAuthPublicConfig } from '~/utils/oauth'
import { parsePaymentMethodsPublicConfig } from '~/utils/payment-methods'
import { parseShippingMethodsPublicConfig } from '~/utils/shipping-methods'
import { parseSentryPublicConfig } from '~/utils/sentry'
import { normalizeShopCurrency, shopCurrencyLabel } from '~/utils/shopCurrency'
import { parseStorefrontFlag, parseStorefrontHeaderStyle } from '~/utils/storefrontExperience'
import { resolveStockEnforced } from '~/utils/shopServices'

const DEFAULT_HEADER_NAV_LINKS: FooterLink[] = [
  { label: 'Produse', to: '/produse' },
  { label: 'Oferte', to: '/oferte' },
  { label: 'Vinde', to: '/vinde' },
  { label: 'Despre Noi', to: '/cms/about-us' },
  { label: 'Contact', to: '/contact' },
]

const DEFAULT_SHOP_LINKS: FooterLink[] = [
  { label: 'Toate produsele', to: '/produse' },
  { label: 'Oferte', to: '/oferte' },
  { label: 'Vinde-ne produsul', to: '/vinde' },
  { label: 'Returnează un produs', to: '/retur' },
  { label: 'Contact', to: '/contact' },
]

export function isPublicSellLink(to: string): boolean {
  const path = String(to || '').split('?')[0].replace(/\/+$/, '')
  return path === '/vinde' || /\/vinde$/.test(path)
}

export function isPublicOffersLink(to: string): boolean {
  const path = String(to || '').split('?')[0].replace(/\/+$/, '')
  return path === '/oferte' || /\/oferte$/.test(path)
}

export function isPublicReturnLink(to: string): boolean {
  const path = String(to || '').split('?')[0].replace(/\/+$/, '')
  return path === '/retur'
}

function footerLinkPath(to: string): string {
  return String(to || '').split('?')[0].replace(/\/+$/, '')
}

function withReturnProductLink(links: FooterLink[]): FooterLink[] {
  const action = { label: 'Returnează un produs', to: '/retur' }
  if (links.some((link) => footerLinkPath(link.to) === '/retur')) return links

  const rewriteIndex = links.findIndex((link) => {
    const path = footerLinkPath(link.to)
    const label = link.label.trim().toLowerCase().normalize('NFD').replace(/\p{M}/gu, '')
    return path === '/cms/retur'
      || path.endsWith('/cms/retur')
      || label === 'returneaza un produs'
  })
  if (rewriteIndex >= 0) {
    const next = [...links]
    next[rewriteIndex] = { ...next[rewriteIndex], label: action.label, to: '/retur' }
    return next
  }

  const contactIndex = links.findIndex((link) => footerLinkPath(link.to) === '/contact')
  if (contactIndex < 0) return [...links, action]
  const next = [...links]
  next.splice(contactIndex, 0, action)
  return next
}

const DEFAULT_INFO_LINKS: FooterLink[] = [
  { label: 'Despre noi', to: '/cms/about-us' },
  { label: 'Întrebări frecvente', to: '/cms/faqs' },
  { label: 'Urmărire comandă', to: '/tracking' },
  { label: 'Informații transport', to: '/cms/informatii-transport' },
  { label: 'Retur / Anulare', to: '/cms/retur-anulare' },
  { label: 'Termeni și condiții', to: '/cms/termeni-si-conditii' },
  { label: 'Confidențialitate', to: '/cms/politica-de-confidentialitate' },
  { label: 'Politica cookies', to: '/cms/politica-de-cookies' },
  { label: 'Contact', to: '/contact' },
]

function resolveShopDisplayName(
  settings: Record<string, string>,
  fallback = 'Magazin',
): string {
  return (
    settings.site_name?.trim()
    || settings.header_brand_text?.trim()
    || settings.company_name?.trim()
    || fallback
  )
}

export const DEFAULT_OFFERS_HOME_TITLE = 'Ofertele momentului'
export const DEFAULT_OFFERS_HOME_SUBTITLE = 'Toate produsele cu reducere, de la cea mai mare.'

export function useSiteSettings() {
  const appConfig = useAppConfig()
  const config = useRuntimeConfig()
  const apiOrigin = config.public.apiOrigin as string
  const siteUrl = config.public.siteUrl as string
  const shopSlug = useShopSlug()
  const { customDomainMode } = useShopDomain()
  const shopApiBasesForRequest = useShopApiBases()
  const settings = useState<Record<string, string>>('site-settings', () => ({}))
  const loaded = useState('site-settings-loaded', () => false)
  const loadedForShop = useState('site-settings-shop', () => '')

  function applyMap(map: Record<string, string>) {
    settings.value = { ...settings.value, ...map }
    loaded.value = true
    loadedForShop.value = shopSlug.value
    applyThemeCss(map.primary_color || settings.value.primary_color || '#2563eb')
  }

  /** Clear cached settings immediately when switching shops (before refetch). */
  function resetForShopChange() {
    settings.value = {}
    loaded.value = false
    loadedForShop.value = ''
  }

  function applyLoadedSettings(slug: string, data: Record<string, string>) {
    settings.value = { ...data }
    loaded.value = true
    loadedForShop.value = slug
    applyThemeCss(data.primary_color || '#2563eb')
  }

  async function readPublicSettings(base: string): Promise<Record<string, string> | null> {
    const response = await $fetch<ApiResponse<Record<string, string>>>(
      `${base}/settings/public`,
      { timeout: 8000 },
    )
    if (!response.success || !response.data || !Object.keys(response.data).length) return null
    return response.data
  }

  async function fetchSettings(force = false) {
    const slug = shopSlug.value
    // Platform / root pages have no shop context
    if (!slug) {
      resetForShopChange()
      loaded.value = true
      return
    }

    const hasSettings = Object.keys(settings.value).length > 0
    if (!force && loaded.value && loadedForShop.value === slug && hasSettings) {
      return
    }

    // Reset when switching shops so keys from the previous tenant cannot leak
    if (loadedForShop.value && loadedForShop.value !== slug) {
      resetForShopChange()
    }

    const bases = shopApiBasesForRequest()

    for (const base of bases) {
      try {
        const data = await readPublicSettings(base)
        if (!data) continue
        applyLoadedSettings(slug, data)
        return
      } catch {
        // The public API host is unreachable from the frontend container.
        // Try the next base, then let the browser retry.
      }
    }

    // A failed server fetch must stay unloaded. Caching it marks the shop as
    // loaded with an empty map, and the browser then skips shop_addons — the
    // admin menu hides Vinde, Retur, Oferte and Calculator.
    if (import.meta.client) {
      loaded.value = true
      loadedForShop.value = slug
    }
  }

  const primaryColor = computed(() => settings.value.primary_color || '#2563eb')
  const themeCss = computed(() => generateThemeCss(primaryColor.value))

  function parseLogoHeight(raw: string | undefined, fallback: number): number {
    const height = Number.parseInt(raw || '', 10)
    if (!Number.isFinite(height) || height <= 0) return fallback
    return Math.min(120, Math.max(24, height))
  }

  function parseHeroHeight(raw: string | undefined, fallback = 420): number {
    const height = Number.parseInt(raw || '', 10)
    if (!Number.isFinite(height) || height <= 0) return fallback
    return Math.min(800, Math.max(200, height))
  }

  function parseHeroSlideDurationMs(raw: string | undefined): number {
    const seconds = Number.parseInt(raw || '', 10)
    if (!Number.isFinite(seconds) || seconds <= 0) return 5000
    return Math.min(60, Math.max(2, seconds)) * 1000
  }

  /** Only shop-configured logos — never a platform/demo brand asset. */
  function resolveConfiguredLogo(...candidates: Array<string | undefined>): string {
    const raw = candidates.map((value) => value?.trim()).find(Boolean)
    return raw ? resolveMediaUrl(raw, apiOrigin) : ''
  }

  const site = computed(() => {
    const displayName = resolveShopDisplayName(settings.value, appConfig.site.name || 'Magazin')
    return {
      name: displayName,
      // Empty until settings load — do not invent tenant marketing copy.
      description: settings.value.site_description?.trim() || '',
      logoHeaderDesktop: resolveConfiguredLogo(
        settings.value.site_logo_header_desktop,
        settings.value.site_logo,
      ),
      logoHeaderDesktopHeight: parseLogoHeight(settings.value.site_logo_header_desktop_height, 40),
      logoHeaderMobile: resolveConfiguredLogo(
        settings.value.site_logo_header_mobile,
        settings.value.site_logo_header_desktop,
        settings.value.site_logo,
      ),
      logoHeaderMobileHeight: parseLogoHeight(settings.value.site_logo_header_mobile_height, 32),
      logoFooter: resolveConfiguredLogo(
        settings.value.site_logo_footer,
        settings.value.site_logo_header_desktop,
        settings.value.site_logo,
      ),
      logoFooterHeight: parseLogoHeight(settings.value.site_logo_footer_height, 36),
      phone: settings.value.contact_phone || appConfig.site.phone || '',
      email: settings.value.contact_email || appConfig.site.email || '',
      address: settings.value.contact_address || appConfig.site.address || '',
    }
  })

  function withCacheBust(url: string, versionSeed: string): string {
    // Chrome caches favicons aggressively; bust when the stored path changes.
    const seed = versionSeed.trim()
    if (
      !seed
      || seed.startsWith('/brand-placeholder')
      || seed.startsWith('/logo.webp')
      || seed.startsWith('/apple-touch-icon')
      || seed.startsWith('/og-default')
    ) {
      return url
    }
    const version = seed.split('/').pop()?.split('?')[0] || seed
    const joiner = url.includes('?') ? '&' : '?'
    return `${url}${joiner}v=${encodeURIComponent(version)}`
  }

  const branding = computed(() => {
    // Neutral platform placeholders only — never a former tenant brand.
    const faviconRaw = settings.value.favicon?.trim() || '/brand-placeholder.webp'
    const appleConfigured = settings.value.apple_touch_icon?.trim() || ''
    const logoSource = (
      (appleConfigured && !appleConfigured.startsWith('/apple-touch-icon') ? appleConfigured : '')
      || settings.value.site_logo_header_mobile?.trim()
      || settings.value.site_logo_header_desktop?.trim()
      || settings.value.site_logo?.trim()
      || appleConfigured
      || '/apple-touch-icon.png'
    )
    const slug = shopSlug.value
    const appleRaw = customDomainMode.value || !slug
      ? '/apple-touch-icon.png'
      : `/${slug}/apple-touch-icon.png`
    const ogRaw = settings.value.og_image?.trim() || '/og-default.jpg'
    return {
      favicon: withCacheBust(resolveMediaUrl(faviconRaw, apiOrigin), faviconRaw),
      faviconType: guessIconMimeType(faviconRaw),
      appleTouchIcon: withCacheBust(appleRaw, logoSource),
      ogImage: resolveAbsoluteMediaUrl(ogRaw, siteUrl, apiOrigin),
      themeColor: primaryColor.value,
      description: site.value.description,
    }
  })

  const headerBrand = computed(() => {
    const fontSize = Number.parseInt(settings.value.header_brand_font_size || '20', 10)
    return {
      show: settings.value.header_brand_text_enabled !== 'false',
      text: (
        settings.value.header_brand_text
        || settings.value.site_name
        || site.value.name
      ).trim(),
      fontFamily: settings.value.header_brand_font_family?.trim() || undefined,
      fontSize: Number.isFinite(fontSize) ? Math.min(48, Math.max(12, fontSize)) : 20,
    }
  })

  const topBannerText = computed(() => settings.value.top_banner_text?.trim() || '')

  const topBannerEnabled = computed(() =>
    settings.value.top_banner_enabled !== 'false' && Boolean(topBannerText.value),
  )

  const showOutOfStockProducts = computed(() =>
    settings.value.show_out_of_stock_products !== 'false',
  )

  const shopCurrency = computed(() => normalizeShopCurrency(settings.value.shop_currency))
  const shopCurrencyShortLabel = computed(() => shopCurrencyLabel(shopCurrency.value))

  const reviewsEnabled = computed(() =>
    settings.value.reviews_enabled !== 'false',
  )

  const recommendedSortEnabled = computed(() =>
    settings.value.product_sort_recommended_enabled === 'true',
  )

  const productsDefaultSort = computed(() => {
    const value = String(settings.value.products_default_sort || '').trim()
    if (
      value === 'createdAt'
      || value === 'discount'
      || value === 'price-asc'
      || value === 'price-desc'
      || value === 'name'
      || value === 'rating'
      || value === 'recommended'
    ) {
      if (!reviewsEnabled.value && value === 'rating') return 'createdAt'
      if (value === 'recommended' && !recommendedSortEnabled.value) return 'createdAt'
      return value
    }
    return 'createdAt'
  })

  const ageVerificationEnabled = computed(() =>
    settings.value.age_verification_enabled === 'true',
  )

  const sellEnabled = computed(() => {
    try {
      const parsed = JSON.parse(settings.value.shop_addons || 'null') as { sell?: boolean } | null
      if (parsed && typeof parsed === 'object' && 'sell' in parsed) return parsed.sell === true
    } catch {
      // fall through to legacy setting
    }
    return settings.value.sell_enabled === 'true' || settings.value.sell_enabled === '1'
  })

  const sellPublic = computed(() =>
    sellEnabled.value
    && (settings.value.sell_public_enabled === 'true' || settings.value.sell_public_enabled === '1'),
  )

  const offersEnabled = computed(() => {
    try {
      const parsed = JSON.parse(settings.value.shop_addons || 'null') as { offers?: boolean } | null
      if (parsed && typeof parsed === 'object' && 'offers' in parsed) return parsed.offers === true
    } catch {
      // fall through to the synced setting
    }
    return settings.value.offers_enabled === 'true' || settings.value.offers_enabled === '1'
  })

  const offersPublic = computed(() =>
    offersEnabled.value
    && (settings.value.offers_public_enabled === 'true' || settings.value.offers_public_enabled === '1'),
  )

  const offersHomeTitle = computed(() =>
    settings.value.offers_home_title?.trim() || DEFAULT_OFFERS_HOME_TITLE,
  )

  const offersHomeSubtitle = computed(() => {
    const value = settings.value.offers_home_subtitle
    if (value == null) return DEFAULT_OFFERS_HOME_SUBTITLE
    return value.trim()
  })

  function withoutHiddenModules(links: FooterLink[]) {
    return links.filter((link) => {
      if (!sellPublic.value && isPublicSellLink(link.to)) return false
      if (!offersPublic.value && isPublicOffersLink(link.to)) return false
      return true
    })
  }

  const returnsPolicy = computed(() => {
    try {
      const parsed = JSON.parse(settings.value.returns_policy || '{}') as {
        enabled?: boolean
        withdrawalDays?: number
        returnShippingPayer?: string
        checkoutNotice?: string
      }
      return {
        enabled: parsed.enabled === true,
        withdrawalDays: Number(parsed.withdrawalDays) || 14,
        returnShippingPayer: parsed.returnShippingPayer || 'by_reason',
        checkoutNotice: String(parsed.checkoutNotice || '').trim(),
      }
    } catch {
      return {
        enabled: false,
        withdrawalDays: 14,
        returnShippingPayer: 'by_reason',
        checkoutNotice: '',
      }
    }
  })

  const returnsModuleEnabled = computed(() => {
    try {
      const parsed = JSON.parse(settings.value.shop_addons || 'null') as { returns?: boolean } | null
      if (parsed && typeof parsed === 'object' && 'returns' in parsed) return parsed.returns === true
    } catch {
      // fall through
    }
    return returnsPolicy.value.enabled
  })

  const generatorsEnabled = computed(() => {
    try {
      const parsed = JSON.parse(settings.value.shop_addons || 'null') as { generators?: boolean } | null
      if (parsed && typeof parsed === 'object' && 'generators' in parsed) return parsed.generators === true
    } catch {
      // fall through
    }
    return settings.value.generators_enabled === 'true' || settings.value.generators_enabled === '1'
  })

  const returnsEnabled = computed(() =>
    returnsModuleEnabled.value && returnsPolicy.value.enabled,
  )

  const productItemRoleEnabled = computed(() =>
    settings.value.product_item_role_enabled === 'true'
    || settings.value.product_item_role_enabled === '1',
  )

  const productItemRolePricesVisible = computed(() =>
    productItemRoleEnabled.value
    && (
      settings.value.product_item_role_prices_visible === 'true'
      || settings.value.product_item_role_prices_visible === '1'
    ),
  )

  const shopServicesEnabled = computed(() =>
    settings.value.shop_services_enabled === 'true'
    || settings.value.shop_services_enabled === '1',
  )

  const stockManagementEnabled = computed(() =>
    resolveStockEnforced(
      settings.value.stock_management_enabled !== 'false',
      shopServicesEnabled.value,
    ),
  )

  const shopServicesCheckoutMode = computed(() => {
    if (!shopServicesEnabled.value) return 'payment' as const
    return settings.value.shop_services_checkout_mode === 'request' ? 'request' as const : 'payment' as const
  })

  const themeSwitchEnabled = computed(() =>
    settings.value.footer_theme_switch_enabled !== 'false',
  )

  const footerBackground = computed(() => {
    const mode = (settings.value.footer_bg_mode || 'default').trim()
    const customColor = settings.value.footer_bg_color?.trim() || primaryColor.value
    const imageRaw = settings.value.footer_bg_image?.trim() || ''
    const imageUrl = imageRaw ? resolveMediaUrl(imageRaw, apiOrigin) : ''
    const overlayEnabled = settings.value.footer_bg_overlay_enabled !== 'false'

    if (mode === 'primary') {
      return {
        mode: 'primary' as const,
        className: '',
        style: { backgroundColor: primaryColor.value } as Record<string, string>,
        showOverlay: false,
      }
    }

    if (mode === 'color') {
      return {
        mode: 'color' as const,
        className: '',
        style: { backgroundColor: customColor } as Record<string, string>,
        showOverlay: false,
      }
    }

    if (mode === 'image' && imageUrl) {
      return {
        mode: 'image' as const,
        className: '',
        style: {
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        } as Record<string, string>,
        showOverlay: overlayEnabled,
      }
    }

    return {
      mode: 'default' as const,
      className: 'bg-brand-900',
      style: undefined as Record<string, string> | undefined,
      showOverlay: false,
    }
  })

  const footerText = computed(() => {
    const text = settings.value.footer_text_color?.trim() || ''
    const heading = settings.value.footer_heading_color?.trim() || ''
    return {
      text: text || null,
      heading: heading || null,
    }
  })

  const heroHeight = computed(() => parseHeroHeight(settings.value.hero_height))
  const heroHeightMobile = computed(() => {
    const raw = settings.value.hero_height_mobile
    if (raw == null || raw === '') return parseHeroHeight(settings.value.hero_height, 360)
    return parseHeroHeight(raw, 360)
  })
  const heroSlideDurationMs = computed(() => parseHeroSlideDurationMs(settings.value.hero_slide_duration))
  const heroCarouselEnabled = computed(() => settings.value.hero_carousel_enabled !== 'false')

  const headerNavLinks = computed(() =>
    withoutHiddenModules(parseFooterLinks(settings.value.header_nav_links, DEFAULT_HEADER_NAV_LINKS)),
  )

  const footer = computed(() => {
    const displayName = site.value.name
    return {
      // No invented about copy — empty until the shop configures it.
      aboutText: settings.value.footer_about_text?.trim() || '',
      shopTitle: settings.value.footer_shop_title || 'Magazin',
      shopLinks: (() => {
        const visible = withoutHiddenModules(parseFooterLinks(settings.value.footer_shop_links, DEFAULT_SHOP_LINKS))
        if (!returnsEnabled.value) return visible.filter((link) => !isPublicReturnLink(link.to))
        return withReturnProductLink(visible)
      })(),
      infoTitle: settings.value.footer_info_title || 'Informații',
      infoLinks: (() => {
        const visible = withoutHiddenModules(parseFooterLinks(settings.value.footer_info_links, DEFAULT_INFO_LINKS))
        if (!returnsEnabled.value) return visible.filter((link) => !isPublicReturnLink(link.to))
        return visible
      })(),
      contactTitle: settings.value.footer_contact_title || 'Contact',
      copyright: settings.value.footer_copyright?.trim()
        || `${displayName}. Toate drepturile rezervate.`,
      badges: parseFooterBadges(settings.value.footer_badges),
      socialLinks: buildFooterSocialItems(parseFooterSocialLinks(settings.value.footer_social_links)),
    }
  })

  const homepageFeatureCards = computed(() =>
    parseHomepageFeatureCards(settings.value.homepage_feature_cards, DEFAULT_HOMEPAGE_FEATURE_CARDS),
  )

  const homepageSections = computed(() =>
    parseHomepageSections(settings.value.homepage_sections, DEFAULT_HOMEPAGE_SECTIONS),
  )

  const homepageCategoriesDisplay = computed(() =>
    parseHomepageCategoriesDisplay(
      settings.value.homepage_categories_display,
      DEFAULT_HOMEPAGE_CATEGORIES_DISPLAY,
    ),
  )

  const googleReviewsEnabled = computed(() =>
    settings.value.google_reviews_enabled === 'true',
  )

  const giftsterGeneratorEnabled = computed(() =>
    settings.value.giftster_generator_enabled === 'true'
    || settings.value.giftster_generator_enabled === '1',
  )

  const googleReviewsMode = computed(() =>
    parseGoogleReviewsMode(settings.value.google_reviews_mode),
  )

  const googleReviewsEmbedUrl = computed(() =>
    parseGoogleReviewsEmbedUrl(settings.value.google_reviews_embed_url),
  )

  const googleBusinessUrl = computed(() =>
    settings.value.google_business_url?.trim() || '',
  )

  const googleReviewsData = computed(() =>
    parseGoogleReviewsData(settings.value.google_reviews_data, EMPTY_GOOGLE_REVIEWS_DATA),
  )

  const checkoutTasks = computed(() =>
    enabledCheckoutTasks(parseCheckoutTasks(settings.value.checkout_tasks)),
  )

  const contactPage = computed(() => ({
    label: settings.value.contact_page_label || 'CONTACT US',
    title: settings.value.contact_page_title || 'Ne poți scrie dacă ai întrebări!',
    locations: parseContactLocations(settings.value.contact_locations, {
      mures: settings.value.contact_location_mures,
      turda: settings.value.contact_location_turda,
    }),
    phone: settings.value.contact_phone || appConfig.site.phone,
    email: settings.value.contact_email || appConfig.site.email,
    formPlaceholder: settings.value.contact_form_placeholder || 'Cu ce te putem ajuta? Scrie-ne fără grijă!',
    gdprText: settings.value.contact_gdpr_text || 'Sunt de acord ca datele mele să fie colectate.',
    gdprLink: settings.value.contact_gdpr_link || '/cms/politica-de-confidentialitate',
    companyName: settings.value.company_name?.trim() || '',
    companyLegalName: settings.value.company_legal_name?.trim() || '',
    companyCui: settings.value.company_cui?.trim() || '',
    companyRegCom: settings.value.company_reg_com?.trim() || '',
    companyRegisteredAddress: settings.value.company_registered_address?.trim() || '',
    companyAdministrator: settings.value.company_administrator?.trim() || '',
    companyContactPerson: settings.value.company_contact_person?.trim() || '',
    companyBank: settings.value.company_bank?.trim() || '',
    companyIbanRon: settings.value.company_iban_ron?.trim() || '',
    companyIbanEur: settings.value.company_iban_eur?.trim() || '',
    companySwift: settings.value.company_swift?.trim() || '',
  }))

  const metaPixel = computed(() => parseMetaPixelConfig(settings.value.meta_pixel))
  const googleAnalytics = computed(() => parseGoogleAnalyticsConfig(settings.value.google_analytics))
  const googleTagManager = computed(() => parseGoogleTagManagerConfig(settings.value.google_tag_manager))
  const googleAds = computed(() => parseGoogleAdsConfig(settings.value.google_ads))
  const tawkTo = computed(() => parseTawkToConfig(settings.value.tawk_to))
  const oauth = computed(() => parseOAuthPublicConfig(settings.value.oauth))
  const paymentMethods = computed(() => parsePaymentMethodsPublicConfig(settings.value.payment_methods))
  const shippingMethods = computed(() => parseShippingMethodsPublicConfig(settings.value.shipping_methods))
  const sentry = computed(() => parseSentryPublicConfig(settings.value.sentry))

  const storefrontLocked = computed(() =>
    settings.value.subscription_storefront_locked === '1',
  )
  const platformMaintenance = computed(() =>
    settings.value.platform_maintenance === '1',
  )
  const shopMaintenance = computed(() =>
    settings.value.shop_maintenance === '1',
  )

  const storefrontCardHoverImage = computed(() =>
    parseStorefrontFlag(settings.value.storefront_card_hover_image),
  )
  const storefrontFlyToCart = computed(() =>
    parseStorefrontFlag(settings.value.storefront_fly_to_cart),
  )
  const storefrontStaggerGrid = computed(() =>
    parseStorefrontFlag(settings.value.storefront_stagger_grid),
  )
  const storefrontImageZoom = computed(() =>
    parseStorefrontFlag(settings.value.storefront_image_zoom),
  )
  const storefrontOrderConfetti = computed(() =>
    parseStorefrontFlag(settings.value.storefront_order_confetti),
  )
  const headerStyle = computed(() =>
    parseStorefrontHeaderStyle(settings.value.storefront_header_style),
  )

  return {
    settings,
    loaded,
    loadedForShop,
    site,
    branding,
    footer,
    contactPage,
    headerNavLinks,
    headerBrand,
    topBannerEnabled,
    topBannerText,
    showOutOfStockProducts,
    shopCurrency,
    shopCurrencyShortLabel,
    productsDefaultSort,
    recommendedSortEnabled,
    stockManagementEnabled,
    ageVerificationEnabled,
    sellEnabled,
    sellPublic,
    offersEnabled,
    offersPublic,
    offersHomeTitle,
    offersHomeSubtitle,
    returnsModuleEnabled,
    generatorsEnabled,
    returnsPolicy,
    returnsEnabled,
    reviewsEnabled,
    productItemRoleEnabled,
    productItemRolePricesVisible,
    shopServicesEnabled,
    shopServicesCheckoutMode,
    themeSwitchEnabled,
    footerBackground,
    footerText,
    heroHeight,
    heroHeightMobile,
    heroSlideDurationMs,
    heroCarouselEnabled,
    homepageFeatureCards,
    homepageSections,
    homepageCategoriesDisplay,
    googleReviewsEnabled,
    giftsterGeneratorEnabled,
    googleReviewsMode,
    googleReviewsEmbedUrl,
    googleBusinessUrl,
    googleReviewsData,
    checkoutTasks,
    primaryColor,
    themeCss,
    metaPixel,
    googleAnalytics,
    googleTagManager,
    googleAds,
    tawkTo,
    oauth,
    paymentMethods,
    shippingMethods,
    sentry,
    storefrontLocked,
    platformMaintenance,
    shopMaintenance,
    storefrontCardHoverImage,
    storefrontFlyToCart,
    storefrontStaggerGrid,
    storefrontImageZoom,
    storefrontOrderConfetti,
    headerStyle,
    fetchSettings,
    applyMap,
    resetForShopChange,
  }
}
